import React, { useState } from 'react';
import { X, UserCheck, Loader2, CheckCircle2, AlertCircle, Truck, Search } from 'lucide-react';
import { veiculoService } from '../../services/veiculoService';
import { supabase } from '../../lib/supabase';

interface TrocarMotoristaVeiculoModalProps {
  isOpen: boolean;
  onClose: () => void;
  veiculo: {
    id: string;
    placa: string;
    motorista_id?: number | null;
    motorista_nome?: string;
  } | null;
  veiculosOptions?: any[];
  pessoasOptions: { id: number; nome: string; ativo?: boolean }[];
  onMotoristaAtualizado: (veiculoId: string, motoristaId: number, motoristaNome: string, newPessoaObj?: any) => void;
}

export const TrocarMotoristaVeiculoModal: React.FC<TrocarMotoristaVeiculoModalProps> = ({
  isOpen,
  onClose,
  veiculo,
  veiculosOptions = [],
  pessoasOptions,
  onMotoristaAtualizado,
}) => {
  const [selectedVeiculoId, setSelectedVeiculoId] = useState<string>('');
  const [selectedMotoristaId, setSelectedMotoristaId] = useState<string>('');
  const [searchMotoristaTerm, setSearchMotoristaTerm] = useState<string>('');
  
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Active vehicle target
  const targetVeiculo = React.useMemo(() => {
    if (veiculo) return veiculo;
    if (selectedVeiculoId) {
      const found = veiculosOptions.find(v => String(v.id) === String(selectedVeiculoId));
      if (found) {
        return {
          id: String(found.id),
          placa: found.placa || String(found.id),
          motorista_id: found.motorista_id,
          motorista_nome: found.motorista_nome,
        };
      }
    }
    return null;
  }, [veiculo, selectedVeiculoId, veiculosOptions]);

  // Sync initial state when modal opens
  React.useEffect(() => {
    if (veiculo) {
      setSelectedVeiculoId(String(veiculo.id));
      setSelectedMotoristaId(veiculo.motorista_id ? String(veiculo.motorista_id) : '');
    } else {
      setSelectedVeiculoId('');
      setSelectedMotoristaId('');
    }
    setSearchMotoristaTerm('');
    setErrorMsg(null);
  }, [veiculo, isOpen]);

  // Update selected driver if target vehicle changes
  React.useEffect(() => {
    if (targetVeiculo && targetVeiculo.motorista_id) {
      setSelectedMotoristaId(String(targetVeiculo.motorista_id));
    }
  }, [targetVeiculo]);

  // Filtragem ao digitar para motoristas no modal
  const displayPessoasModal = React.useMemo(() => {
    if (!searchMotoristaTerm.trim()) return pessoasOptions;
    const term = searchMotoristaTerm.toLowerCase().trim();
    return pessoasOptions.filter(p => p.nome && p.nome.toLowerCase().includes(term));
  }, [pessoasOptions, searchMotoristaTerm]);

  if (!isOpen) return null;

  const handleSave = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    setErrorMsg(null);

    if (!targetVeiculo) {
      setErrorMsg('Por favor, selecione um veículo.');
      return;
    }

    if (!selectedMotoristaId) {
      setErrorMsg('Selecione um motorista da lista.');
      return;
    }

    setSaving(true);

    try {
      const finalMotoristaId = Number(selectedMotoristaId);
      const found = pessoasOptions.find(p => String(p.id) === selectedMotoristaId);
      const finalMotoristaNome = found ? found.nome : `Motorista #${finalMotoristaId}`;

      // Actualiza o cadastro oficial do veículo no Supabase
      await veiculoService.update(targetVeiculo.id, {
        motorista_id: finalMotoristaId,
      });

      // Notifica o componente pai para atualizar estados e selecioná-lo
      onMotoristaAtualizado(targetVeiculo.id, finalMotoristaId, finalMotoristaNome);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao vincular motorista ao veículo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-white">Vincular / Trocar Motorista do Veículo</h3>
              <p className="text-2xs text-slate-400 flex items-center gap-1 font-mono">
                <Truck className="w-3 h-3 text-amber-400" />
                Placa: <strong className="text-white">{targetVeiculo ? (targetVeiculo.placa || targetVeiculo.id) : 'Selecione o Veículo'}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {errorMsg && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Seleção do Veículo caso não venha selecionado */}
          {!veiculo && (
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700 uppercase">
                Selecione o Veículo
              </label>
              <select
                value={selectedVeiculoId}
                onChange={(e) => setSelectedVeiculoId(e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 font-medium bg-white focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="">-- Escolha um veículo --</option>
                {veiculosOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.placa ? `Placa: ${v.placa}` : `Veículo #${v.id}`} {v.motorista_nome ? `(${v.motorista_nome})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Info do Veículo */}
          {targetVeiculo && (
            <div className="p-3 bg-amber-50/60 border border-amber-200/80 rounded-xl space-y-1">
              <p className="text-xs text-amber-900">
                Motorista Atualmente Vinculado:
              </p>
              <p className="text-sm font-bold text-amber-950 flex items-center gap-1.5">
                <UserCheck className="w-4 h-4 text-amber-600" />
                {targetVeiculo.motorista_nome || 'Nenhum motorista atribuído'}
              </p>
            </div>
          )}

          {/* Seleção de Motorista Existente */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700 uppercase">
              Selecione o Motorista ({displayPessoasModal.length} de {pessoasOptions.length})
            </label>

            {/* Input de Busca Rápida ao Digitar */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={searchMotoristaTerm}
                  onChange={(e) => setSearchMotoristaTerm(e.target.value)}
                  placeholder="Buscar motorista ao digitar..."
                  className="w-full pl-8 pr-8 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-red-500 bg-white"
                />
                {searchMotoristaTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchMotoristaTerm('')}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {}}
                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-2xs"
              >
                <Search className="w-3.5 h-3.5" />
                <span>Buscar</span>
              </button>
            </div>

            <select
              value={selectedMotoristaId}
              onChange={(e) => setSelectedMotoristaId(e.target.value)}
              className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 font-medium bg-white focus:ring-2 focus:ring-red-500 outline-none"
            >
              <option value="">-- Selecione o motorista na lista --</option>
              {displayPessoasModal.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2.5 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-md transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Vincular ao Veículo
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
